import { Box, Stack } from '@mui/material';
import { compact } from 'lodash';
import { useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';

import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { LinkTabProps } from '@/components/navigation/NavigationTabs';
import { PageHeader } from '@/components/navigation/PageHeader';
import { useMatchingRoutes } from '@/hooks/useMatchingRoutes';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { AISuggestionsV2Banner } from '@/modules/aiSuggestions/components/AISuggestionsV2Banner/AISuggestionsV2Banner';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import { AddIndividualOrOrganization } from './AddIndividualOrOrganization';
import { DownloadFiduciaryReportMenuItem } from './components/DownloadFiduciaryReportMenuItem/DownloadFiduciaryReportMenuItem';

export function ClientPeoplePage() {
  const householdId = useRequiredParam('householdId');
  const unlinkedButtonPortalRef = useRef<HTMLDivElement>(null);
  const canEdit = useCanEditHouseholdData();

  const tabs: LinkTabProps[] = useMemo(() => {
    return compact([
      {
        display: 'Individuals & organizations',
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PEOPLE_ORGANIZATIONS,
          {
            householdId,
          }
        ),
      },
      {
        display: 'Family tree',
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_FAMILY_TREE,
          {
            householdId,
          }
        ),
      },
      {
        display: 'Client team',
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PEOPLE_CLIENT_TEAM,
          {
            householdId,
          }
        ),
      },
    ]);
  }, [householdId]);

  const { isMatchingRoute: isFamilyTreeTab } = useMatchingRoutes([
    ROUTE_KEYS.HOUSEHOLD_DETAILS_FAMILY_TREE,
  ]);

  const action = useMemo(() => {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Portal target for UnlinkedIndividualsButton */}
        {isFamilyTreeTab && <div ref={unlinkedButtonPortalRef} />}
        <AddIndividualOrOrganization householdId={householdId} />
        {!isFamilyTreeTab && (
          <ContextMenuButton>
            <DownloadFiduciaryReportMenuItem householdId={householdId} />
          </ContextMenuButton>
        )}
      </Stack>
    );
  }, [isFamilyTreeTab, householdId]);

  return (
    <Stack height="100%">
      <PageHeader heading="People" tabs={tabs} action={action} />
      {canEdit && (
        // Empty wrapper collapses when the banner returns null, so dismissed
        // / inapplicable households don't get phantom top padding above the
        // People sub-page content.
        <Box sx={{ px: 2, pt: 2, '&:empty': { display: 'none' } }}>
          <AISuggestionsV2Banner />
        </Box>
      )}
      <Outlet context={{ unlinkedButtonPortalRef }} />
    </Stack>
  );
}
